import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import { cn, formatMoney, formatNumber } from '@/platform/lib/utils';
import {
    Badge,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import { MinusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import BooleanIcon from './BooleanIcon';

const COLORS = {
    Hoog: 'red',
    Laag: 'green',
    Herzien: 'yellow',
    Combinatie: 'blue',
    Geen: 'grey',
};

const EmploymentPeriodsTable = ({ data, year }) => {
    const [pickedYear, setPickedYear] = useState(null);

    const years = Object.keys(data?.data || {});
    const selectedYear = pickedYear ?? (year || years.at(-1)) ?? null;
    const periodData = data?.data?.[selectedYear] ?? [];

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <TableHeaderCell textAlign='center'>
                                Periode
                            </TableHeaderCell>
                        </TooltipTrigger>
                        <TooltipContent>Periode (Maandnummer)</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <TableHeaderCell textAlign='center'>
                                SrtIV
                            </TableHeaderCell>
                        </TooltipTrigger>
                        <TooltipContent>
                            Soort Inkomstenverhouding
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <TableHeaderCell textAlign='center'>
                                CdAard
                            </TableHeaderCell>
                        </TooltipTrigger>
                        <TooltipContent>
                            Code aard arbeidsverhouding
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <TableHeaderCell textAlign='center'>
                                OT
                            </TableHeaderCell>
                        </TooltipTrigger>
                        <TooltipContent>Onbepaalde tijd</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <TableHeaderCell textAlign='center'>
                                SA
                            </TableHeaderCell>
                        </TooltipTrigger>
                        <TooltipContent>Schriftelijk aangegaan</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <TableHeaderCell textAlign='center'>
                                OC
                            </TableHeaderCell>
                        </TooltipTrigger>
                        <TooltipContent>Oproep Contract</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <TableHeaderCell textAlign='center'>
                                C. uren
                            </TableHeaderCell>
                        </TooltipTrigger>
                        <TooltipContent>Contract uren</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <TableHeaderCell textAlign='center'>
                                V. uren
                            </TableHeaderCell>
                        </TooltipTrigger>
                        <TooltipContent>Verloonde uren</TooltipContent>
                    </Tooltip>
                    <TableHeaderCell textAlign='center'>
                        Premieloon
                    </TableHeaderCell>
                    <TableHeaderCell textAlign='center'>Premie</TableHeaderCell>
                    <TableHeaderCell textAlign='center'>
                        Percentage
                    </TableHeaderCell>
                    <TableHeaderCell textAlign='center'>Type</TableHeaderCell>
                    <TableHeaderCell content='' textAlign='right'>
                        Mog. Besparing
                    </TableHeaderCell>
                </TableRow>
            </TableHeader>
            <TableBody>
                {!periodData?.data && <NoDataRow numberOfColumns={13} />}

                {periodData?.data?.map(employmentPeriod => (
                    <TableRow
                        key={employmentPeriod.id}
                        error={employmentPeriod.is_projection}
                    >
                        <TableCell textAlign='center'>
                            {employmentPeriod?.period?.number}
                        </TableCell>
                        <TableCell textAlign='center'>
                            {employmentPeriod.income_relationship_type}
                        </TableCell>
                        <TableCell textAlign='center'>
                            {employmentPeriod.nature_of_employment}
                        </TableCell>
                        <TableCell textAlign='center'>
                            <BooleanIcon
                                positive={
                                    employmentPeriod.contract_type ===
                                    'permanent'
                                }
                            />
                        </TableCell>
                        <TableCell textAlign='center'>
                            <BooleanIcon
                                positive={employmentPeriod.written_contract}
                            />
                        </TableCell>
                        <TableCell textAlign='center'>
                            <BooleanIcon
                                positive={employmentPeriod.on_call_contract}
                            />
                        </TableCell>
                        <TableCell textAlign='center'>
                            {employmentPeriod.contract_hours}
                        </TableCell>
                        <TableCell textAlign='center'>
                            {employmentPeriod.paid_hours}
                            <span className='text-xs text-gray-400'>
                                {' '}
                                (
                                {employmentPeriod.paid_hours_percentage > 0
                                    ? '+'
                                    : ''}
                                {employmentPeriod.paid_hours_percentage}
                                %)
                            </span>
                        </TableCell>
                        <TableCell textAlign='right'>
                            {formatMoney(employmentPeriod.premieloon_awf)}
                        </TableCell>
                        <TableCell textAlign='right'>
                            {formatMoney(employmentPeriod.premie_awf)}
                        </TableCell>
                        <TableCell textAlign='center'>
                            {employmentPeriod.premie_awf_percentage}%
                        </TableCell>
                        <TableCell textAlign='center'>
                            <Badge
                                color={COLORS[employmentPeriod.premie_awf_type]}
                            >
                                {employmentPeriod.premie_awf_type}
                            </Badge>
                        </TableCell>
                        <TableCell textAlign='right'>
                            {employmentPeriod.potential_savings !== null ? (
                                formatMoney(employmentPeriod.potential_savings)
                            ) : (
                                <MinusIcon className='inline-flex size-5' />
                            )}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
            <TableFooter>
                <TableRow>
                    <TableCell colSpan={6} />
                    <TableCell textAlign='center'>
                        {periodData?.totals?.contract_hours}
                    </TableCell>
                    <TableCell textAlign='center'>
                        {periodData?.totals?.paid_hours}
                        <span className='text-xs text-gray-400'>
                            {' '}
                            (
                            {periodData?.totals?.paid_hours_percentage > 0
                                ? '+'
                                : ''}
                            {periodData?.totals?.paid_hours_percentage}
                            %)
                        </span>
                    </TableCell>
                    <TableCell textAlign='right'>
                        {formatMoney(periodData?.totals?.premieloon_awf || 0)}
                    </TableCell>
                    <TableCell textAlign='right'>
                        {formatMoney(periodData?.totals?.premie_awf || 0)}
                    </TableCell>
                    <TableCell textAlign='center'>
                        {formatNumber(
                            periodData?.totals?.premie_awf_percentage || 0
                        )}{' '}
                        %
                    </TableCell>
                    <TableCell />
                    <TableCell textAlign='right'>
                        {formatMoney(
                            periodData?.totals?.potential_savings || 0
                        )}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell textAlign='center' colSpan={13}>
                        <ul className='flex flex-row items-center justify-center divide-x-2'>
                            {years?.map(year => (
                                <li
                                    key={year}
                                    onClick={() => setPickedYear(year)}
                                    className={cn(
                                        'hover:text-primary-600 cursor-pointer px-4',
                                        year == selectedYear &&
                                            'text-primary-500'
                                    )}
                                >
                                    {year}
                                </li>
                            ))}
                        </ul>
                    </TableCell>
                </TableRow>
            </TableFooter>
        </Table>
    );
};

export default EmploymentPeriodsTable;
